import { injectIntl } from "react-intl";
import { useEffect, useState } from "react";
import TableHeader from "src/components/TableHeader";
import {
  CREATIVES_TABLE_TITLE_WIDTHS,
  CREATIVES_TABLE_TITLES,
  CREATIVES_TABLE_TITLE_SORT,
} from "src/helpers/Constants";
import { CreativeTableSortType } from "src/helpers/Enums";
import Pagination from "src/components/Pagination";

const SORT_FIELD_TO_COLUMN_INDEX = {
  name: 0,
  totalRevenue: 1,
  score: 2,
  numberOfSend: 3,
  offer_name: 4,
  last_used_at: 5,
};

const CreativesTable = (props) => {
  const [selectedOfferIds, setSelectedOfferIds] = useState(() => new Set());

  const toggleOfferSelection = (offerId) => {
    setSelectedOfferIds((prev) => {
      const next = new Set(prev);
      if (next.has(offerId)) {
        next.delete(offerId);
      } else {
        next.add(offerId);
      }
      return next;
    });
  };

  const activeSortColumnIndex = SORT_FIELD_TO_COLUMN_INDEX[props.sortField];
  const isAscending = props.sortOrder === "asc";
  const offersSortState =
    CREATIVES_TABLE_TITLE_SORT?.map((item, index) => ({
      ...item,
      active: activeSortColumnIndex === index ? isAscending : false,
    })) ?? CREATIVES_TABLE_TITLE_SORT;

  const formatRevenue = (amount) => {
    if (amount == null || isNaN(amount)) {
      return "-";
    }
    let formatted = Number(amount).toFixed(2);
    formatted = formatted.replace(/\.00$/, "");
    return `$${formatted}`;
  };

  const MONTH_SHORT = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];

  const formatLastUsedAt = (iso) => {
    if (iso == null || iso === "") {
      return "-";
    }
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) {
      return "-";
    }
    const month = MONTH_SHORT[d.getUTCMonth()];
    const day = String(d.getUTCDate()).padStart(2, "0");
    const year = d.getUTCFullYear();
    return `${month} ${day}, ${year}`;
  };

  const selectionCount = selectedOfferIds.size;
  const hasSelection = selectionCount > 0;

  const clearSelection = () => {
    setSelectedOfferIds(new Set());
  };

  const handleOpenDeleteSelected = () => {
    const ids = Array.from(selectedOfferIds);
    if (!ids.length) {
      return;
    }
    props.handleOpenDeleteSelected?.(ids);
  };

  useEffect(() => {
    if (props.deleteNonce > 0) {
      setSelectedOfferIds(new Set());
    }
  }, [props.deleteNonce]);

  return (
    <div className={"w-full flex flex-col justify-between h-full"}>
      <div className={"w-full flex flex-col gap-3"}>
        {hasSelection ? (
          <div
            className="flex w-full flex-[1_0_0] self-stretch items-center justify-between rounded-lg border border-[#F2EBFB] bg-[#F9F5FF] py-2 px-[10px]"
            style={{ fontFamily: "Inter, sans-serif" }}
          >
            <div
              className="text-[12px] font-semibold leading-6 text-[#7E50EA]"
              style={{ fontFamily: "Inter, sans-serif" }}
            >
              {selectionCount} {selectionCount === 1 ? "offer" : "offers"}{" "}
              selected
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                className="inline-flex h-[30px] items-center justify-center gap-1 rounded-md border border-[#E5E7F0] bg-white px-3 text-[12px] font-semibold text-[#DD2E2E]"
                style={{
                  boxShadow: "0 1px 2px 0 rgba(21, 21, 31, 0.05)",
                }}
                onClick={handleOpenDeleteSelected}
              >
                <img
                  alt=""
                  src="/media/icons/deleteIcon.svg"
                  className="h-[14px] w-[14px] shrink-0"
                />
                Delete Selected
              </button>
              <button
                type="button"
                className="inline-flex h-[30px] items-center justify-center gap-1 rounded-md border border-[#E5E7F0] bg-white px-3 text-[12px] font-semibold leading-normal text-[#15151F]"
                style={{ boxShadow: "0 1px 2px 0 rgba(21, 21, 31, 0.05)" }}
                onClick={clearSelection}
              >
                Clear Selection
              </button>
            </div>
          </div>
        ) : (
          <TableHeader
            titles={CREATIVES_TABLE_TITLES}
            widths={CREATIVES_TABLE_TITLE_WIDTHS}
            sort={offersSortState}
            sortType={CreativeTableSortType}
            setList={props.setList}
            list={props.list}
            textAlignments={[null, "right", null, null, null, null]}
            showIconForColumns={[null, true, null, null, null, null]}
            onSortChange={props.onSortChange}
          />
        )}
        <div className="TableWrapper">
          <table className="Table" style={{ tableLayout: "fixed" }}>
            <tbody>
              {props.list?.map((offer, index) => {
                const isOfferSelected = selectedOfferIds.has(offer._id);
                return (
                  <tr
                    key={index}
                    className="border-b h-[60px] hover:bg-[#F6F6F9]"
                  >
                    <td
                      className="py-3 pl-[6px] text-[12px] leading-[14px] font-semibold overflow-hidden"
                      style={{ width: CREATIVES_TABLE_TITLE_WIDTHS[0] }}
                    >
                      <div className="flex items-center gap-[6px]">
                        <label className="inline-flex shrink-0 cursor-pointer">
                          <input
                            type="checkbox"
                            className="sr-only"
                            checked={isOfferSelected}
                            onChange={() => toggleOfferSelection(offer._id)}
                            aria-label={`Select offer ${offer.name ?? ""}`}
                          />
                          <span
                            className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-[4px] border ${
                              isOfferSelected
                                ? "border-[#7F56D9] bg-[#F9F5FF]"
                                : "border-[#D0D5DD] bg-white"
                            }`}
                          >
                            {isOfferSelected && (
                              <img
                                alt="Logo"
                                src="/media/icons/check.svg"
                                className="w-3 h-3"
                              />
                            )}
                          </span>
                        </label>
                        <div className="flex min-w-0 flex-[1_0_0] flex-col items-start justify-center gap-[6px]">
                          <div className="flex w-full min-w-0 items-center gap-1">
                            <span className="min-w-0 flex-1 truncate">
                              {offer.name ? offer.name : "-"}
                            </span>
                            <span
                              className={`inline-flex shrink-0 items-center h-[18px] px-[6px] rounded-[34px] text-[11px] font-semibold ${
                                offer.everflow_creative_id
                                  ? "bg-[#53A852] text-white"
                                  : "bg-[#F2F3F7] text-[#8185A8]"
                              }`}
                              style={{ fontFamily: "Inter" }}
                              title={
                                offer.everflow_creative_id
                                  ? `Everflow ${offer.everflow_creative_id}`
                                  : "Not matched to Everflow"
                              }
                            >
                              {offer.everflow_creative_id
                                ? "Matched"
                                : "Unmatched"}
                            </span>
                            {offer.link_id && (
                              <img
                                alt="Logo"
                                src="/media/icons/link-hyperlink-icon.svg"
                                className="w-[14px] h-[14px] shrink-0"
                              />
                            )}
                          </div>
                          <div className="h-px self-stretch bg-[#DFE2EB]" />
                          <div
                            className="self-stretch min-w-0 truncate text-[12px] font-normal leading-4 text-[#8185A8]"
                            style={{ fontFamily: "Inter" }}
                          >
                            {offer.message ? offer.message : "-"}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td
                      className="py-3 px-3 border-r text-[12px] text-end leading-[14px] font-semibold"
                      style={{ width: CREATIVES_TABLE_TITLE_WIDTHS[1] }}
                    >
                      <div
                        className={`inline-flex items-center h-[18px] px-[6px] rounded-[34px] text-white text-[11px] font-semibold ${
                          offer.totalRevenue != null &&
                          Number(offer.totalRevenue) === 0
                            ? "bg-[#8185A8]"
                            : "bg-[#53A852]"
                        }`}
                        style={{ fontFamily: "Inter" }}
                      >
                        {formatRevenue(offer.totalRevenue)}
                      </div>
                    </td>
                    <td
                      className="py-3 px-5 border-r text-[12px] leading-[14px] font-semibold"
                      style={{ width: CREATIVES_TABLE_TITLE_WIDTHS[2] }}
                    >
                      {offer.score == null
                        ? "0"
                        : Number.isInteger(Number(offer.score))
                        ? Number(offer.score)
                        : Number(offer.score).toFixed(2)}
                    </td>
                    <td
                      className="py-3 px-5 border-r text-[12px] leading-[14px] font-semibold"
                      style={{ width: CREATIVES_TABLE_TITLE_WIDTHS[3] }}
                    >
                      {offer.numberOfSend}
                    </td>
                    <td
                      className="py-3 px-5 border-r text-[12px] leading-[14px] font-semibold"
                      style={{ width: CREATIVES_TABLE_TITLE_WIDTHS[4] }}
                    >
                      <div className="flex items-center gap-1">
                        {" "}
                        <img
                          alt={"Logo"}
                          src={"/media/icons/layout-alt-03.svg"}
                          className={"w-[14px] h-[14px]"}
                        />
                        {offer.offer_name}
                      </div>
                    </td>
                    <td
                      className="py-3 px-5 border-r text-[12px] font-medium leading-[14px] text-[#8185A8]"
                      style={{
                        width: CREATIVES_TABLE_TITLE_WIDTHS[5],
                        fontFamily: "Inter",
                      }}
                    >
                      {formatLastUsedAt(offer.last_used_at)}
                    </td>
                    <td
                      className="flex py-3 px-4"
                      style={{ width: CREATIVES_TABLE_TITLE_WIDTHS[6] }}
                    >
                      <button
                        className="StrategyEditButton"
                        onClick={() => props.handleOpenEditCreative(offer)}
                      >
                        <img
                          alt={"Logo"}
                          src={"/media/icons/editIcon.svg"}
                          className={"w-4 h-4"}
                        />
                      </button>
                      <button
                        className="StrategyDeleteButton"
                        onClick={() => props.handleOpenDeleteCreative(offer)}
                      >
                        <img
                          alt={"Logo"}
                          src={"/media/icons/deleteIcon.svg"}
                          className={"w-4 h-4"}
                        />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
      <Pagination
        currentPage={props.currentPage}
        totalPages={props.totalPages}
        totalCount={props.totalCount}
        pageSize={props.pageSize}
        onPageChange={props.onPageChange}
      />
    </div>
  );
};

export default injectIntl(CreativesTable);
